/**
 * Two-level header for the Supplier Performance table.
 *
 * Split out of PerformanceTable (which was past the 500-line limit). The
 * frozen-column `left` offsets come from `frozenColumns` so the header and
 * PerformanceTableRow cannot drift apart.
 *
 * Customer review 11 Sep (item 4): no Supplier Code column at the top
 * level. A supplier is known by a different code at each customer site (one supplier
 * ALLOYS UK has six), so the row that stands for the supplier as a whole has no
 * single code; each site's code is shown on its breakdown row, and on the
 * Customer cell when the row covers exactly one site.
 *
 * CR-4547: the Warehouse column follows the tenant's Warehouse filter
 * visibility (Admin Setup → Platform → Filter Visibility, CR-4802). A tenant
 * that holds no warehouse data, or has switched the filter off, does not get
 * a column of dashes. Header, rows and `frozenColumns().columnCount` all read
 * the same flag so the colSpans cannot drift.
 *
 * CR-8667: every column header sorts (asc → desc → default order), with the
 * shared SortableTh caret. The group headings above them (Quality, Cost, …)
 * do not — they are not columns. Each `field` is the row's serialized name,
 * which is what the server's `?sort=` takes (performanceSort.ts).
 */
import { CSSProperties, ReactNode } from 'react';
import { useFilterVisibility } from '../../hooks/useFilterVisibility';
import { SortableTh } from '../../components/common/SortableTh';
import { PerformanceSort } from './performanceSort';

interface PerformanceTableHeaderProps {
  viewerIsSupplier: boolean;
  showSupplierSite: boolean;
  qualityLabel: string;
  /** CR-8667: the sort in effect (null = default order) and the header click. */
  sort?: PerformanceSort | null;
  onSort?: (field: string) => void;
}

/** Widths of the frozen leading columns, in px. */
const SUPPLIER_W = 240;
const SITE_W = 200;
const CUSTOMER_W = 200;

/**
 * `left` offsets of the frozen columns for the current layout.
 *
 * @param viewerIsSupplier - supplier-side page (no Supplier column)
 * @param showSupplierSite - supplier-side GROUP view (Supplier Site column)
 * @param showWarehouse - CR-4547: whether the tenant shows the Warehouse column
 * @returns the offsets and the total number of columns the table renders
 */
export function frozenColumns(viewerIsSupplier: boolean, showSupplierSite: boolean, showWarehouse = true) {
  const lead = viewerIsSupplier ? (showSupplierSite ? SITE_W : 0) : SUPPLIER_W;
  return {
    customerLeft: lead,
    ratingLeft: lead + CUSTOMER_W,
    /** Every <td> in a row: leads + Customer + Rating + [Warehouse] + 8/7 quality + 5 cost + 5 PO + 4 GRN + CO2. */
    columnCount: (viewerIsSupplier ? (showSupplierSite ? 1 : 0) : 1) + 2 + (showWarehouse ? 1 : 0)
      + (viewerIsSupplier ? 7 : 8) + 5 + 5 + 4 + 1,
  };
}

interface ColumnProps {
  /** The sort in effect and the click handler; absent = a plain header. */
  sorting?: { sort: PerformanceSort | null; onSort: (field: string) => void };
  field: string;
  children: ReactNode;
  className: string;
  minWidth: number;
  align?: 'left' | 'center';
  rowSpan?: number;
  left?: number;
  title?: string;
}

/** One column header: sortable when the table takes a sort, plain otherwise. */
function Column({ sorting, field, children, className, minWidth, align = 'center', rowSpan, left, title }: ColumnProps) {
  const style: CSSProperties = { minWidth: `${minWidth}px`, ...(left !== undefined ? { left: `${left}px` } : {}) };
  if (!sorting) {
    return <th className={`${className} text-${align}`} style={style} rowSpan={rowSpan} title={title}>{children}</th>;
  }
  return (
    <SortableTh
      field={field}
      sortField={sorting.sort?.field ?? null}
      sortDirection={sorting.sort?.direction ?? 'asc'}
      onSort={sorting.onSort}
      align={align}
      className={className}
      style={style}
      rowSpan={rowSpan}
      title={title}
      // Solid: this header is sticky, and a see-through hover lets the rows show through.
      hoverClassName="hover:bg-accent"
    >
      {children}
    </SortableTh>
  );
}

export function PerformanceTableHeader({
  viewerIsSupplier,
  showSupplierSite,
  qualityLabel,
  sort = null,
  onSort,
}: PerformanceTableHeaderProps) {
  // CR-4547 / CR-4802: the tenant shape decides whether Warehouse is a column.
  const { warehouse: showWarehouse } = useFilterVisibility();
  const { customerLeft, ratingLeft } = frozenColumns(viewerIsSupplier, showSupplierSite, showWarehouse);
  const sorting = onSort ? { sort, onSort } : undefined;
  const lead = 'p-3 font-semibold sticky bg-muted z-10';
  const sub = 'p-2 text-xs font-medium bg-muted';

  return (
    // CR-4313: Sticky headers so column labels stay visible when scrolling
    <thead className="sticky top-0 z-20">
      {/* Top-level header groups */}
      {/* Solid bg (not /80 + backdrop-blur) so scrolled rows don't bleed through the sticky header. */}
      <tr className="border-b bg-muted">
        {!viewerIsSupplier && (
          <Column sorting={sorting} field="company_name" className={`${lead} left-0`} minWidth={SUPPLIER_W} align="left" rowSpan={2}>
            Supplier
          </Column>
        )}
        {showSupplierSite && (
          <Column sorting={sorting} field="supplier_site" className={`${lead} left-0`} minWidth={SITE_W} align="left" rowSpan={2}>
            Supplier Site
          </Column>
        )}
        <Column
          sorting={sorting}
          field="customer_scope"
          className={lead}
          minWidth={CUSTOMER_W}
          left={customerLeft}
          rowSpan={2}
          title={viewerIsSupplier ? undefined : 'The customer sites this row covers — hover a cell for the list and each site’s supplier code'}
        >
          Customer
        </Column>
        <Column sorting={sorting} field="combined_score" className={`${lead} border-r`} minWidth={100} left={ratingLeft} rowSpan={2}>
          Rating
        </Column>
        {showWarehouse && (
          <Column sorting={sorting} field="warehouse_name" className="p-3 font-semibold bg-muted" minWidth={120} rowSpan={2}>
            Warehouse
          </Column>
        )}
        {/* SI-16: Response Score is pooled across customers by the serializer
            (no per-customer scope), so the supplier-side page hides it rather
            than show the same number on every row (R-133). CO2 is scoped per
            customer since the Customer review (msg 16) and shows on both. */}
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: viewerIsSupplier ? '700px' : '780px' }}
          colSpan={viewerIsSupplier ? 7 : 8}
        >
          Quality
        </th>
        {/* Cost: value, reject value and the three cost-of-quality figures
            (Customer review 11 Sep, msg 10 — legacy's formula, no click-through). */}
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: '580px' }}
          colSpan={5}
        >
          Cost
        </th>
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: '500px' }}
          colSpan={5}
        >
          Delivery (PO Line)
        </th>
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: '400px' }}
          colSpan={4}
        >
          Delivery (GRN)
        </th>
        <th
          className="p-3 text-center font-semibold border-l"
          style={{ minWidth: '150px' }}
          colSpan={1}
        >
          CO2 Emissions
        </th>
      </tr>
      {/* Second-level headers */}
      <tr className="border-b bg-muted">
        <Column sorting={sorting} field="qty_received" className={`${sub} border-l`} minWidth={100}>Qty Received</Column>
        <Column sorting={sorting} field="reject_qty" className={sub} minWidth={100}>Qty Rejected</Column>
        <Column sorting={sorting} field="customer_liability_ppm" className={sub} minWidth={120}>Customer Liability {qualityLabel}</Column>
        <Column sorting={sorting} field="supplier_liability_ppm" className={sub} minWidth={120}>Supplier Liability {qualityLabel}</Column>
        <Column sorting={sorting} field="total_ppm" className={sub} minWidth={100}>Total {qualityLabel}</Column>
        <Column sorting={sorting} field="ncr_count" className={sub} minWidth={80}>No. of NCRs</Column>
        <Column sorting={sorting} field="return_count" className={sub} minWidth={80}>Returns</Column>
        {!viewerIsSupplier && (
          <Column sorting={sorting} field="response_score" className={sub} minWidth={80}>Response Score</Column>
        )}
        <Column sorting={sorting} field="total_value" className={`${sub} border-l`} minWidth={120}>Total Value</Column>
        <Column sorting={sorting} field="reject_value" className={sub} minWidth={100}>Reject Value</Column>
        <Column
          sorting={sorting}
          field="customer_cost_of_quality"
          className={sub}
          minWidth={120}
          title="Cost of Quality per NCR (Admin Setup → Quality) × Customer Liability NCRs + their reject value"
        >
          Customer Cost of Quality
        </Column>
        <Column
          sorting={sorting}
          field="supplier_cost_of_quality"
          className={sub}
          minWidth={120}
          title="Cost of Quality per NCR (Admin Setup → Quality) × Supplier Liability (or undetermined) NCRs + their reject value"
        >
          Supplier Cost of Quality
        </Column>
        <Column sorting={sorting} field="total_cost_of_quality" className={sub} minWidth={120} title="Customer + Supplier Cost of Quality">
          Total Cost of Quality
        </Column>
        <Column sorting={sorting} field="po_line_count" className={`${sub} border-l`} minWidth={80}>PO Lines</Column>
        <Column sorting={sorting} field="otif_count" className={sub} minWidth={80}>OTIF</Column>
        <Column sorting={sorting} field="arrears_count" className={sub} minWidth={80}>Arrears</Column>
        <Column sorting={sorting} field="otif_score_percentage" className={sub} minWidth={100}>OTIF Score</Column>
        <Column sorting={sorting} field="qty_diff" className={sub} minWidth={80}>Qty Diff</Column>
        <Column sorting={sorting} field="grn_count" className={`${sub} border-l`} minWidth={80}>No. of GRNs</Column>
        <Column sorting={sorting} field="ontime_count" className={sub} minWidth={80}>Ontime</Column>
        <Column sorting={sorting} field="late_count" className={sub} minWidth={80}>Late</Column>
        <Column sorting={sorting} field="early_count" className={sub} minWidth={80}>Early</Column>
        <Column
          sorting={sorting}
          field="carbon_emission"
          className={`${sub} border-l`}
          minWidth={150}
          title="Sum of the CO2 captured on the supplier's ASNs in this period"
        >
          Total CO2 Emissions
        </Column>
      </tr>
    </thead>
  );
}
